import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import axios from 'axios'
import ThemeToggle from './ThemeToggle'
import LanguageSelector from './LanguageSelector'

export default function Navbar() {
  const { t } = useTranslation()
  const location = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [me, setMe] = useState<{ isAdmin: boolean; organizationId: number | null } | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    let mounted = true
    axios
      .get('/api/auth/me')
      .then((r) => {
        if (!mounted) return
        setMe(r.data?.user ?? null)
      })
      .catch(() => {
        if (!mounted) return
        setMe(null)
      })
    return () => {
      mounted = false
    }
  }, [location.pathname])

  const logout = async () => {
    await axios.post('/api/auth/logout')
    setMe(null)
  }

  const navLinks = [
    { path: '/', key: 'home' },
    { path: '/solutions', key: 'solutions' },
    { path: '/a-propos', key: 'about' },
    { path: '/blog', key: 'blog' },
    { path: '/support', key: 'support' },
  ]

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 dark:bg-background-dark/90 backdrop-blur-md shadow-lg'
          : 'bg-white/80 dark:bg-background-dark/80 backdrop-blur-md'
      } border-b border-slate-200 dark:border-slate-800`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20 items-center">
          <Link to="/" className="flex items-center">
            <div className="flex flex-col items-start">
              <span className="text-primary font-bold text-2xl tracking-tighter leading-none">
                NDT
              </span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-slate-400">
                NiceDay Technologies
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8 items-center">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`hover:text-primary font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-primary'
                    : ''
                }`}
              >
                {t(`nav.${link.key}`)}
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-primary text-white px-6 py-2.5 rounded-full font-semibold hover:bg-opacity-90 transition-all shadow-lg shadow-primary/20"
            >
              {t('nav.contact')}
            </Link>
            {me?.organizationId && (
              <Link
                to="/portal"
                className="px-5 py-2.5 rounded-full font-semibold border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors"
              >
                {t('nav.portal')}
              </Link>
            )}
            {me?.isAdmin && (
              <Link
                to="/admin"
                className="px-5 py-2.5 rounded-full font-semibold border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors"
              >
                {t('nav.admin')}
              </Link>
            )}
            {!me && (
              <Link
                to="/login"
                className="px-5 py-2.5 rounded-full font-semibold border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors"
              >
                {t('nav.login')}
              </Link>
            )}
            {me && (
              <button
                onClick={logout}
                className="px-5 py-2.5 rounded-full font-semibold border border-slate-200 dark:border-slate-700 hover:border-primary hover:text-primary transition-colors"
              >
                {t('nav.logout')}
              </button>
            )}
            <LanguageSelector />
            <ThemeToggle />
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center gap-4">
            <LanguageSelector />
            <ThemeToggle />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <span className="material-icons text-3xl">
                {mobileMenuOpen ? 'close' : 'menu'}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-background-dark"
          >
            <div className="px-4 py-4 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block hover:text-primary font-medium transition-colors"
                >
                  {t(`nav.${link.key}`)}
                </Link>
              ))}
              {me?.organizationId && (
                <Link
                  to="/portal"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block hover:text-primary font-medium transition-colors"
                >
                  {t('nav.portal')}
                </Link>
              )}
              {me?.isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block hover:text-primary font-medium transition-colors"
                >
                  {t('nav.admin')}
                </Link>
              )}
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block bg-primary text-white px-6 py-2.5 rounded-full font-semibold text-center hover:bg-opacity-90 transition-all"
              >
                {t('nav.contact')}
              </Link>
              {!me && (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block border border-slate-200 dark:border-slate-700 px-6 py-2.5 rounded-full font-semibold text-center hover:border-primary hover:text-primary transition-colors"
                >
                  {t('nav.login')}
                </Link>
              )}
              {me && (
                <button
                  onClick={() => {
                    logout()
                    setMobileMenuOpen(false)
                  }}
                  className="block w-full border border-slate-200 dark:border-slate-700 px-6 py-2.5 rounded-full font-semibold text-center hover:border-primary hover:text-primary transition-colors"
                >
                  {t('nav.logout')}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  )
}
